import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { useAsyncEffect } from '@xylabs/sdk-react'
import { Quadkey } from '@xyo-network/sdk-xyoworld-js'
import BigNumber from 'bn.js'
import { useContext, useEffect, useState } from 'react'

import { CurrencyBalanceContext, useAuction } from '../Contexts'
import { parseBigNumber } from '../parseBigNumber'
import { shiftBase10 } from '../shiftBase10'
import uniswapLogo from './img/uniswap.svg'
import XyoTextField from './XyoTextField'

interface BidDialogProps {
  onClose?: () => void
  open: boolean
  quadkey?: Quadkey
}

const BidDialogOpen: React.FC<BidDialogProps> = (props) => {
  const { quadkey, open, onClose } = props
  const { balance, minterAllowance } = useContext(CurrencyBalanceContext)
  const { auction, refresh } = useAuction(quadkey)
  const [minimumBid, setMinimumBid] = useState<BigNumber>()
  const [currentBid, setCurrentBid] = useState<BigNumber>()
  const [amount, setAmount] = useState<string>()
  const [fee, setFee] = useState<BigNumber>()
  const [total, setTotal] = useState<BigNumber>()

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isMounted) => {
      const minimumBid = await auction?.getMinimumBid()
      const hasBid = await auction?.fetchHasBid()
      const currentBid = hasBid ? await auction?.getCurrentBid() : undefined
      if (isMounted()) {
        setMinimumBid(minimumBid)
        setCurrentBid(currentBid)
        setAmount(shiftBase10(minimumBid, -18)?.addn(1).toString())
      }
    },
    [auction],
  )

  const bidAmount = amount ? shiftBase10(parseBigNumber(amount), 18) ?? undefined : undefined

  const onBidClicked = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    if (bidAmount) {
      await auction?.actions.bid(bidAmount)
    }
    onClose?.()
    refresh?.()
  }

  const bidDisabled = bidAmount === undefined || balance?.lt(bidAmount) || minterAllowance?.lt(bidAmount)

  const onRefreshClicked = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    refresh?.()
  }

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const amount = parseBigNumber(event.target.value)
    setAmount(amount.toString())
  }

  useEffect(() => {
    if (amount) {
      if (currentBid) {
        const amountBigNumber = shiftBase10(parseBigNumber(amount), 18) ?? new BigNumber(0)
        const fee = amountBigNumber.sub(currentBid).muln(0.08)
        const total = amountBigNumber.add(fee)
        setFee(fee)
        setTotal(total)
      } else {
        const total = shiftBase10(parseBigNumber(amount), 18) ?? new BigNumber(0)
        setTotal(total)
      }
    }
  }, [amount, currentBid])

  useEffect(() => {
    setAmount(shiftBase10(minimumBid, -18)?.toString() ?? '0')
  }, [minimumBid])

  return (
    <Dialog
      onClose={(event: React.MouseEvent) => {
        event.stopPropagation()
        onClose?.()
      }}
      open={open}
    >
      <DialogTitle>Bid</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column">
          <Box display="flex">
            <Typography variant="body2">Please select how much you want to bid</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" padding={1}>
            <Typography variant="body2">Current: </Typography>
            <Typography variant="body2">{shiftBase10(currentBid, -18)?.toString() ?? '-'}</Typography>
          </Box>
          <Box padding={1}>
            <XyoTextField fullWidth label="Bid Amount" onChange={onChange} value={amount ?? '0'} variant="outlined" />
          </Box>
          <Box display="flex" justifyContent="space-between" padding={1}>
            <Typography variant="body2">Previous bidder reward: </Typography>
            <Typography variant="body2">{shiftBase10(fee, -18)?.toString() ?? '-'}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" padding={1}>
            <Typography variant="body2">Total: </Typography>
            <Typography variant="body2">{shiftBase10(total, -18)?.toString() ?? '-'}</Typography>
          </Box>
          <Box display="flex" justifyContent="center" paddingTop={4}>
            <Button
              endIcon={<img alt="Uniswap" src={uniswapLogo} />}
              href="https://info.uniswap.org/pair/0xa986f2a12d85c44429f574ba50c0e21052b18ba1"
              target="_blank"
              variant="outlined"
            >
              Get more XYO on{' '}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" width="100%">
          {onClose ? (
            <Box>
              <Button
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation()
                  onClose?.()
                }}
                variant="text"
              >
                Close
              </Button>
            </Box>
          ) : null}
          <Box flexGrow={1}></Box>
          <Box>
            <Button onClick={onRefreshClicked} variant="text">
              Refresh
            </Button>
          </Box>
          <Box>
            <Button disabled={bidDisabled} onClick={onBidClicked} variant="text">
              Bid
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

const BidDialog: React.FC<BidDialogProps> = (props) => {
  const { open } = props
  if (open) {
    return <BidDialogOpen {...props} />
  } else {
    return null
  }
}

export default BidDialog
